import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { BarcodeRendererService } from '../../services/barcode-renderer.service';
import { LabelPdfService } from '../../services/label-pdf.service';
import { Symbology, BarcodeRequest, LabelTemplate } from '../../types/barcodes';
import { GlobalSearchService } from 'app/services/globalsearchservice.service';

@Component({
  selector: 'app-barcode-generator',
  templateUrl: './barcode-generator.component.html',
  styleUrls: ['./barcode-generator.component.scss']
})
export class BarcodeGeneratorComponent implements OnInit, OnDestroy {
  form: FormGroup;
  previews: Array<{ value: string; symbology: Symbology; dataUrl?: string; selected: boolean }> = [];
  templates: LabelTemplate[] = [];
  loading = false;
  color: string = 'blue';

  constructor(
    private fb: FormBuilder,
    private renderer: BarcodeRendererService,
    private pdf: LabelPdfService,
    private globalSearchService: GlobalSearchService,
  ) {
    this.form = this.fb.group({
      symbology: ['code128', Validators.required],
      width: [256],
      height: [64],
      displayValue: [true],
      batchList: ['TEST12345', Validators.required],
      templateId: ['avery-5160', Validators.required]
    });
  }

  async ngOnInit() {
    this.color = this.globalSearchService.getColor();
    const resp = await fetch('assets/label-templates/avery.json');
    const data = await resp.json();
    this.templates = data.templates || [];
  }

  ngOnDestroy(): void {}

  async generatePreview(): Promise<void> {
    this.loading = true;
    try {
      const requests: BarcodeRequest[] = this.buildRequests();
      this.previews = [];
      for (const req of requests) {
        const result = await this.renderer.render(req);
        const dataUrl = result.canvas ? result.canvas.toDataURL('image/png') : result.dataUrl;
        this.previews.push({ value: req.value, symbology: req.symbology, dataUrl, selected: true });
      }
    } finally {
      this.loading = false;
    }
  }

  selectAll(sel: boolean): void {
    this.previews = this.previews.map(p => ({ ...p, selected: sel }));
  }

  async exportPng(): Promise<void> {
    for (const p of this.previews.filter(x => x.selected && x.dataUrl)) {
      const a = document.createElement('a');
      a.href = p.dataUrl!;
      a.download = `${p.symbology}-${p.value}.png`;
      a.click();
    }
  }

  async exportSvg(): Promise<void> {
    // Fallback: render to canvas and skip SVG if not supported by adapter
    await this.exportPng();
  }

  async exportPdf(): Promise<void> {
    const tpl = this.templates.find(t => t.id === this.form.value.templateId);
    if (!tpl) return;
    // If displayValue is already rendered inside the barcode image, avoid duplicating text below
    const addSeparateText = !this.form.value.displayValue;
    const images = this.previews
      .filter(p => p.selected && p.dataUrl)
      .map(p => ({ pngDataUrl: p.dataUrl!, text: addSeparateText ? p.value : undefined }));
    const blob = await this.pdf.generate(tpl, images);
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'labels.pdf';
    a.click();
    URL.revokeObjectURL(url);
  }

  private buildRequests(): BarcodeRequest[] {
    const v = this.form.value;
    const list = (v.batchList as string || '').split(/\r?\n/).map(s => s.trim()).filter(Boolean);
    return list.map((val: string) => ({
      value: val,
      symbology: v.symbology,
      width: v.width,
      height: v.height,
      displayValue: v.displayValue,
    } as BarcodeRequest));
  }
}


